import React, { useState, useMemo, useEffect, memo, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Chip,
  Avatar,
  TextField,
  InputAdornment,
  IconButton,
  Tooltip,
  Card,
  CardContent,
  LinearProgress,
  TablePagination,
  Snackbar,
  Button,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import ContactsIcon from '@mui/icons-material/ContactsOutlined';
import SearchIcon from '@mui/icons-material/Search';
import PhoneIcon from '@mui/icons-material/Phone';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import ContentCopyIcon from '@mui/icons-material/ContentCopyOutlined';
import EmailIcon from '@mui/icons-material/EmailOutlined';
import PlaceIcon from '@mui/icons-material/PlaceOutlined';
import DownloadIcon from '@mui/icons-material/Download';
import { useAllEmployeeDetailsWithResigned } from '../../hooks/useEmployees';
import { TH, TH_CENTER, TD, TD_CENTER, Row, TableWrap, TABLE_STYLE, CHIP_SX } from '../common/tableStyles';

const ROWS_PER_PAGE_OPTIONS = [25, 50, 100];

// ---- FORTHEYE soft-card tokens (shared with Employees / employee portal) ----
const CARD_SX = {
  p: { xs: 2, sm: 2.5, md: 3 },
  borderRadius: '22px',
  bgcolor: '#fff',
  border: '1px solid #eef0f4',
  boxShadow: '0 6px 24px rgba(17,24,39,0.05)',
};
const ACCENT = '#2f6bff'; // primary blue
const GREEN = '#25ac5a'; // WhatsApp / Active green
const GRADIENT_BTN = {
  textTransform: 'none', fontWeight: 700, borderRadius: '12px',
  background: 'linear-gradient(135deg,#3b82f6,#2f6bff)',
  '&:hover': { background: 'linear-gradient(135deg,#2f6bff,#2557d6)' },
};

const SearchField = styled(TextField)(() => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: '12px',
    backgroundColor: '#f8fafc',
    '&:hover fieldset': { borderColor: ACCENT },
  },
}));

// Neutral outlined quick-action buttons (matches the directory mock): light
// bordered squares that pick up their accent colour on hover.
const ACT_BTN = { width: 32, height: 32, borderRadius: '9px', border: '1px solid #e5e7eb', bgcolor: '#fff', color: '#64748b', transition: 'all .14s ease' };
const CALL_BTN_SX = { ...ACT_BTN, '&:hover': { color: ACCENT, borderColor: ACCENT, bgcolor: `${ACCENT}0f` } };
const WA_BTN_SX = { ...ACT_BTN, '&:hover': { color: GREEN, borderColor: GREEN, bgcolor: `${GREEN}0f` } };
const COPY_BTN_SX = { ...ACT_BTN, '&:hover': { color: '#334155', borderColor: '#94a3b8' } };

const STATUS_FILTERS = ['Active', 'Inactive', 'All'];

function useDebouncedValue(value, delay) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

// ---- Phone helpers -------------------------------------------------------
// Numbers are stored free-text (+91, spaces, dashes, leading 0, or bare digits),
// so we reduce to the 10-digit national number for tel:/wa.me links and only
// treat a real Indian mobile (10 digits starting 6-9) as dialable — this rejects
// the "0000000000" default and landlines that would otherwise build dead links.
const digitsOf = (p) => (p || '').toString().replace(/\D/g, '');
const national10 = (p) => {
  const d = digitsOf(p);
  return d.length > 10 ? d.slice(-10) : d;
};
const isMobile10 = (p) => {
  const d = national10(p);
  return d.length === 10 && /^[6-9]/.test(d);
};
const hasRealNumber = (p) => {
  const d = digitsOf(p);
  return d.length > 0 && !/^0+$/.test(d);
};
const toTel = (p) => `+91${national10(p)}`;
const toWa = (p) => `91${national10(p)}`;
const fmtPhone = (p) => {
  if (isMobile10(p)) {
    const d = national10(p);
    return `+91 ${d.slice(0, 5)} ${d.slice(5)}`;
  }
  return (p || '').toString().trim(); // landline / odd format — show as stored
};

// Colourful, deterministic avatars (matches the directory mock): a fixed palette
// hashed by employee_no so each person keeps the same colour across sort/filter,
// plus two-letter initials (first + last name).
const AVATAR_COLORS = ['#2f6bff', '#7c3aed', '#0891b2', '#db2777', '#ea580c', '#16a34a', '#d97706', '#4f46e5', '#0d9488', '#c026d3'];
const colorFor = (key) => {
  const s = (key || '?').toString();
  let h = 0;
  for (let i = 0; i < s.length; i += 1) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return AVATAR_COLORS[h % AVATAR_COLORS.length];
};
const initialsOf = (name) => {
  const parts = (name || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};
const AVATAR_LG = { width: 36, height: 36, fontSize: '0.8rem', fontWeight: 700, color: '#fff' };

// Robust clipboard copy: navigator.clipboard is undefined on plain HTTP and in
// many in-app WebViews, so fall back to a hidden textarea + execCommand and
// report success ONLY when a copy actually happened (never a false "Copied").
function copyToClipboard(text) {
  if (navigator.clipboard?.writeText) {
    return navigator.clipboard.writeText(text).then(() => true, () => legacyCopy(text));
  }
  return Promise.resolve(legacyCopy(text));
}
function legacyCopy(text) {
  try {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.setAttribute('readonly', '');
    ta.style.position = 'fixed';
    ta.style.top = '-1000px';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return ok;
  } catch (e) {
    return false;
  }
}

// Active / Inactive pill derived from the `resigned` flag.
const StatusChip = ({ resigned }) => (
  <Chip
    label={resigned ? 'Inactive' : 'Active'}
    size="small"
    sx={{
      fontWeight: 700, borderRadius: '8px', height: 22, fontSize: '0.7rem',
      ...(resigned
        ? { bgcolor: '#f1f5f9', color: '#64748b' }
        : { bgcolor: '#e7f7ee', color: '#15924b' }),
    }}
  />
);

// Quick-action cluster (Call / WhatsApp / Copy) reused by row + mobile card.
const ContactActions = memo(function ContactActions({ phone, onCopy }) {
  if (!hasRealNumber(phone)) {
    return <Typography variant="caption" color="text.secondary">No number</Typography>;
  }
  // Landline / non-mobile: copy only (a tel:/wa.me link would be broken).
  if (!isMobile10(phone)) {
    return (
      <Tooltip title="Copy number">
        <IconButton size="small" onClick={() => onCopy(phone)} sx={COPY_BTN_SX}>
          <ContentCopyIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    );
  }
  return (
    <Box sx={{ display: 'flex', gap: 0.75 }}>
      <Tooltip title="Call">
        <IconButton size="small" component="a" href={`tel:${toTel(phone)}`} sx={CALL_BTN_SX}>
          <PhoneIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title="WhatsApp">
        <IconButton
          size="small"
          component="a"
          href={`https://wa.me/${toWa(phone)}`}
          target="_blank"
          rel="noopener noreferrer"
          sx={WA_BTN_SX}
        >
          <WhatsAppIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title="Copy number">
        <IconButton size="small" onClick={() => onCopy(phone)} sx={COPY_BTN_SX}>
          <ContentCopyIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Box>
  );
});

// One table row, memoized. Clicking the name opens the profile; it carries full
// keyboard/AT parity (role/tabIndex/aria-label/Enter+Space) like EmployeeRow. The
// phone stays selectable so the row itself is deliberately not a link.
const ContactRow = memo(function ContactRow({ emp, onOpen, onCopy }) {
  const openProfile = () => onOpen(emp.employee_no);
  return (
    <Row>
      <td style={TD}>
        <Chip label={emp.employee_no || 'N/A'} size="small" sx={CHIP_SX} />
      </td>
      <td style={TD}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Avatar sx={{ ...AVATAR_LG, bgcolor: colorFor(emp.employee_no || emp.name) }}>{initialsOf(emp.name)}</Avatar>
          <Box>
            <Typography
              variant="body2"
              fontWeight={600}
              role="link"
              tabIndex={0}
              aria-label={`View profile of ${emp.name || 'employee'}`}
              onClick={openProfile}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openProfile(); } }}
              sx={{ cursor: 'pointer', outline: 'none', '&:hover, &:focus-visible': { color: ACCENT, textDecoration: 'underline' } }}
            >
              {emp.name || 'N/A'}
            </Typography>
            <Typography variant="caption" color="text.secondary">{emp.designation || '—'}</Typography>
          </Box>
        </Box>
      </td>
      <td style={TD_CENTER}>
        <StatusChip resigned={emp.resigned} />
      </td>
      <td style={TD}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>{emp.department || '—'}</Typography>
        <Typography variant="caption" color="text.secondary">
          {(emp.location || emp.branch_name) ? `📍 ${emp.location || emp.branch_name}` : '—'}
        </Typography>
      </td>
      <td style={TD}>
        {emp.email ? (
          <Typography
            component="a"
            href={`mailto:${emp.email}`}
            variant="body2"
            sx={{ color: '#475569', textDecoration: 'none', '&:hover': { color: ACCENT, textDecoration: 'underline' } }}
          >
            {emp.email}
          </Typography>
        ) : <Typography variant="body2" color="text.secondary">—</Typography>}
      </td>
      <td style={TD}>
        <Typography variant="body2" sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums', fontFamily: 'monospace', mb: 0.5 }}>
          {fmtPhone(emp.mobile_number) || '—'}
        </Typography>
        <ContactActions phone={emp.mobile_number} onCopy={onCopy} />
      </td>
    </Row>
  );
});

const Contacts = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobileView = useMediaQuery(theme.breakpoints.down('md'));
  const { data: emp = [], isFetching } = useAllEmployeeDetailsWithResigned();

  const [searchInput, setSearchInput] = useState('');
  const searchQuery = useDebouncedValue(searchInput, 250);
  const [activeDept, setActiveDept] = useState('All');
  const [statusFilter, setStatusFilter] = useState('Active');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [snack, setSnack] = useState({ open: false, msg: '' });

  // Unique, sorted department list for the filter chips (skip blanks).
  const departments = useMemo(() => {
    const set = new Set(emp.map((e) => (e.department || '').trim()).filter(Boolean));
    return ['All', ...[...set].sort((a, b) => a.localeCompare(b))];
  }, [emp]);

  const activeCount = useMemo(() => emp.filter((e) => !e.resigned).length, [emp]);
  const inactiveCount = useMemo(() => emp.filter((e) => e.resigned).length, [emp]);
  const withMobile = useMemo(() => emp.filter((e) => isMobile10(e.mobile_number)).length, [emp]);
  const statusCount = { Active: activeCount, Inactive: inactiveCount, All: emp.length };

  // Status + department + text/phone search. The phone pass is digits-only so a
  // number query matches regardless of how it is stored.
  const processed = useMemo(() => {
    let result = emp;
    if (statusFilter === 'Active') result = result.filter((e) => !e.resigned);
    else if (statusFilter === 'Inactive') result = result.filter((e) => e.resigned);
    if (activeDept !== 'All') {
      result = result.filter((e) => (e.department || '').trim() === activeDept);
    }
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      const digits = searchQuery.replace(/\D/g, '');
      result = result.filter((e) =>
        e.name?.toLowerCase().includes(q) ||
        e.employee_no?.toString().toLowerCase().includes(q) ||
        e.designation?.toLowerCase().includes(q) ||
        e.department?.toLowerCase().includes(q) ||
        e.location?.toLowerCase().includes(q) ||
        e.email?.toLowerCase().includes(q) ||
        (digits && digitsOf(e.mobile_number).includes(digits))
      );
    }
    return result;
  }, [emp, statusFilter, activeDept, searchQuery]);

  const paginated = useMemo(
    () => processed.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [processed, page, rowsPerPage]
  );

  // Snap back to page 1 whenever the result set changes.
  useEffect(() => { setPage(0); }, [searchQuery, activeDept, statusFilter, rowsPerPage]);

  const handleOpenProfile = useCallback(
    (employeeNo) => navigate(`/salary/breakdown/${employeeNo}`),
    [navigate]
  );

  const handleCopy = useCallback((phone) => {
    const text = fmtPhone(phone);
    if (!text) return;
    copyToClipboard(text).then((ok) =>
      setSnack({ open: true, msg: ok ? `Copied ${text}` : 'Could not copy — long-press the number to copy' })
    );
  }, []);

  const downloadExcel = () => {
    const rows = processed.map((e) => ({
      'Emp No': e.employee_no,
      'Name': e.name,
      'Designation': e.designation,
      'Status': e.resigned ? 'Inactive' : 'Active',
      'Department': e.department,
      'Location': e.location || e.branch_name,
      'Email': e.email,
      'Mobile': e.mobile_number,
    }));
    const worksheet = XLSX.utils.json_to_sheet(rows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Contacts');
    XLSX.writeFile(workbook, 'employee_contacts.xlsx');
  };

  const pagination = processed.length > 0 && (
    <TablePagination
      component="div"
      count={processed.length}
      page={page}
      onPageChange={(_, newPage) => setPage(newPage)}
      rowsPerPage={rowsPerPage}
      onRowsPerPageChange={(e) => setRowsPerPage(parseInt(e.target.value, 10))}
      rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
      labelRowsPerPage="Rows:"
    />
  );

  const renderMobileCard = (e) => (
    <Card key={e.id || e.employee_no} elevation={0} sx={{ ...CARD_SX, mb: 2, p: 1 }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
          <Avatar sx={{ bgcolor: colorFor(e.employee_no || e.name), color: '#fff', fontWeight: 700, width: 48, height: 48, mr: 2, fontSize: '1rem' }}>
            {initialsOf(e.name)}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              fontWeight={700}
              role="link"
              tabIndex={0}
              aria-label={`View profile of ${e.name || 'employee'}`}
              onClick={() => handleOpenProfile(e.employee_no)}
              onKeyDown={(ev) => { if (ev.key === 'Enter' || ev.key === ' ') { ev.preventDefault(); handleOpenProfile(e.employee_no); } }}
              sx={{ cursor: 'pointer', lineHeight: 1.2, outline: 'none', '&:focus-visible': { color: ACCENT, textDecoration: 'underline' } }}
            >
              {e.name || 'N/A'}
            </Typography>
            <Typography variant="body2" color="text.secondary">{e.designation || '—'}</Typography>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
            <StatusChip resigned={e.resigned} />
            <Chip label={e.employee_no || 'N/A'} size="small" sx={CHIP_SX} />
          </Box>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, mb: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary' }}>
            <PlaceIcon sx={{ fontSize: 18 }} />
            <Typography variant="body2">{e.department || '—'}{(e.location || e.branch_name) ? ` · ${e.location || e.branch_name}` : ''}</Typography>
          </Box>
          {e.email && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary' }}>
              <EmailIcon sx={{ fontSize: 18 }} />
              <Typography variant="body2" component="a" href={`mailto:${e.email}`} sx={{ color: 'inherit', textDecoration: 'none', wordBreak: 'break-all' }}>
                {e.email}
              </Typography>
            </Box>
          )}
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, pt: 1.5, borderTop: '1px solid #eef0f4' }}>
          <Typography sx={{ fontWeight: 700, fontFamily: 'monospace', fontVariantNumeric: 'tabular-nums' }}>
            {fmtPhone(e.mobile_number) || '—'}
          </Typography>
          <ContactActions phone={e.mobile_number} onCopy={handleCopy} />
        </Box>
      </CardContent>
    </Card>
  );

  return (
    <Box sx={{ p: { xs: 2, md: 4 } }}>
      {/* Header card */}
      <Paper elevation={0} sx={{ ...CARD_SX, mb: { xs: 2, md: 3 } }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            justifyContent: 'space-between',
            alignItems: { xs: 'flex-start', md: 'center' },
            gap: 2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.75 }}>
            <Box sx={{ width: 52, height: 52, borderRadius: '16px', bgcolor: `${ACCENT}14`, color: ACCENT, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <ContactsIcon />
            </Box>
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                <Typography sx={{ fontWeight: 800, fontSize: { xs: '1.35rem', md: '1.7rem' }, color: '#111827', letterSpacing: '-0.02em', lineHeight: 1.15 }}>
                  Contacts
                </Typography>
                <Chip
                  label={processed.length}
                  sx={{ fontWeight: 700, borderRadius: '8px', bgcolor: '#eef4ff', color: ACCENT, border: 'none' }}
                />
              </Box>
              <Typography variant="body2" sx={{ color: '#6b7280' }}>
                Company phone directory · tap a number to call, WhatsApp or copy
              </Typography>
              <Typography variant="caption" sx={{ color: '#94a3b8', display: 'block', mt: 0.25 }}>
                {emp.length} people · {activeCount} active · {inactiveCount} inactive · {withMobile} with mobile
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, width: { xs: '100%', md: 'auto' } }}>
            <Button variant="contained" disableElevation onClick={downloadExcel} startIcon={<DownloadIcon />} sx={GRADIENT_BTN}>
              Export
            </Button>
            <SearchField
              placeholder="Search by name, ID, department or phone number…"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              size="small"
              sx={{ minWidth: { sm: '280px' } }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
        </Box>

        {/* Status filter (Active / Inactive / All) */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mt: 2 }}>
          <Typography variant="caption" sx={{ color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', mr: 0.5 }}>
            Status
          </Typography>
          {STATUS_FILTERS.map((s) => {
            const active = s === statusFilter;
            const color = s === 'Active' ? GREEN : s === 'Inactive' ? '#64748b' : ACCENT;
            return (
              <Chip
                key={s}
                label={`${s} (${statusCount[s]})`}
                onClick={() => setStatusFilter(s)}
                variant={active ? 'filled' : 'outlined'}
                sx={{
                  fontWeight: 700, borderRadius: '10px', cursor: 'pointer',
                  ...(active
                    ? { bgcolor: color, color: '#fff', '&:hover': { bgcolor: color, filter: 'brightness(0.95)' } }
                    : { color, borderColor: `${color}66`, '&:hover': { borderColor: color, bgcolor: `${color}0f` } }),
                }}
              />
            );
          })}
        </Box>

        {/* Department filter chips */}
        {departments.length > 1 && (
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1.5 }}>
            {departments.map((d) => {
              const active = d === activeDept;
              return (
                <Chip
                  key={d}
                  label={d}
                  onClick={() => setActiveDept(d)}
                  variant={active ? 'filled' : 'outlined'}
                  sx={{
                    fontWeight: 600, borderRadius: '10px', cursor: 'pointer',
                    ...(active
                      ? { bgcolor: ACCENT, color: '#fff', '&:hover': { bgcolor: '#2557d6' } }
                      : { color: '#475569', borderColor: '#e5e7eb', '&:hover': { borderColor: ACCENT, color: ACCENT } }),
                  }}
                />
              );
            })}
          </Box>
        )}

        {isFetching && (
          <Box sx={{ width: '100%', mt: 2 }}>
            <LinearProgress sx={{ height: 4, borderRadius: 2 }} />
          </Box>
        )}
      </Paper>

      {processed.length > 0 ? (
        isMobileView ? (
          <Box sx={{ maxHeight: '70vh', overflowY: 'auto', p: 1 }}>
            {paginated.map(renderMobileCard)}
            {pagination}
          </Box>
        ) : (
          <Paper elevation={0} sx={{ ...CARD_SX, p: { xs: 1.5, md: 2 } }}>
            <TableWrap>
              <table style={TABLE_STYLE}>
                <thead>
                  <tr>
                    <th style={TH}>Emp.No</th>
                    <th style={TH}>Name</th>
                    <th style={TH_CENTER}>Status</th>
                    <th style={TH}>Department</th>
                    <th style={TH}>Email</th>
                    <th style={TH}>Contact</th>
                  </tr>
                </thead>
                <tbody>
                  {paginated.map((e) => (
                    <ContactRow
                      key={e.id || e.employee_no}
                      emp={e}
                      onOpen={handleOpenProfile}
                      onCopy={handleCopy}
                    />
                  ))}
                </tbody>
              </table>
            </TableWrap>
            {pagination}
          </Paper>
        )
      ) : (
        <Paper elevation={0} sx={{ ...CARD_SX, display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 1.5, py: 8 }}>
          <Box sx={{ width: 56, height: 56, borderRadius: '16px', bgcolor: `${ACCENT}14`, color: ACCENT, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <ContactsIcon />
          </Box>
          <Typography variant="h6" sx={{ color: '#111827', fontWeight: 700 }}>
            {isFetching ? 'Loading contacts…' : 'No contacts found'}
          </Typography>
          <Typography variant="body2" sx={{ color: '#6b7280' }}>
            {searchQuery || activeDept !== 'All' || statusFilter !== 'Active'
              ? 'No employees match your filters.'
              : 'Employee contacts will appear here once available.'}
          </Typography>
        </Paper>
      )}

      <Snackbar
        open={snack.open}
        autoHideDuration={2200}
        onClose={() => setSnack({ open: false, msg: '' })}
        message={snack.msg}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
};

export default Contacts;
